import {
  faCircleInfo,
  faCircleMinus,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import {
  faArrowDown,
  faCheckCircle,
  faGrid2,
  faTooth,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/videa-components/src/index';
import { cn } from '@repo/videa-components/src/lib/utils';
import type { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { useAtom } from 'jotai';
import { capitalize } from 'lodash';

import type { TProviderTreatment } from '@/react-query/insights/types';
import { usePreferences } from '@/react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { FmxLinkButtonCell } from '../../components/FmxLinkButtonCell';
import { PatientName } from '../../components/PatientName';

import { selectedInsightsPatientInfoAtom } from './atoms';
import { RecommendationPinCell } from './RecommendationPinCell';

export const useClinicalProviderTableColumns = ({
  isCurated,
}: {
  isCurated?: boolean;
}): ColumnDef<TProviderTreatment, unknown>[] => {
  const [, setSelectedInsightsPatientInfo] = useAtom(
    selectedInsightsPatientInfoAtom,
  );
  const { trackEvent } = useTrackEvent();
  const { data: preferences } = usePreferences();

  const handlePatientNameClick = (patientInfo: TProviderTreatment) => {
    trackEvent(mixpanelEvents.insightsPmsInfoSheetOpen, {
      patientName: patientInfo.pmsPatientDisplayName,
      estimatedVisitId: patientInfo.estimatedVisitId,
      providerId: patientInfo.pmsProviderVideaId,
    });

    setSelectedInsightsPatientInfo({
      name: patientInfo.pmsPatientDisplayName,
      estimatedVisitId: patientInfo?.estimatedVisitId ?? undefined,
      apptDate: patientInfo.apptDate,
      patientId: patientInfo.pmsPatientId,
    });
  };

  const columns: ColumnDef<TProviderTreatment, unknown>[] = [
    {
      accessorKey: 'recommendationPinId',
      header: '',
      maxSize: 50,
      cell: RecommendationPinCell,
      enableSorting: true,
      sortingFn: (a, b) => {
        const isAPinned = !!a.original.recommendationPinId;
        const isBPinned = !!b.original.recommendationPinId;
        if (isAPinned !== isBPinned) {
          return isAPinned ? -1 : 1;
        }
        return 0;
      },
      filterFn: (row, filterValue) => {
        if (filterValue === undefined) return true;
        return row.original.recommendationPinId !== null;
      },
    },
    {
      accessorKey: 'apptDate',
      header: 'Appointment Date',
      cell: ({ row }) => {
        // Create date in local timezone by adding time component
        const localDate = new Date(row.original.apptDate + 'T00:00:00');
        return <div>{format(localDate, 'MM/dd/yyyy')}</div>;
      },
      sortingFn: (a, b, desc) => {
        const aDate = new Date(a.original.apptDate);
        const bDate = new Date(b.original.apptDate);
        return desc
          ? bDate.getTime() - aDate.getTime()
          : aDate.getTime() - bDate.getTime();
      },
    },
    {
      accessorKey: 'pmsPatientDisplayName',
      header: 'Patient Name',
      cell: ({ row }) => {
        return (
          <Button
            variant="link"
            size="link"
            className="vai-w-auto"
            onClick={() => handlePatientNameClick(row.original)}
          >
            <PatientName patientName={row.original.pmsPatientDisplayName} />
          </Button>
        );
      },
    },
    {
      accessorKey: 'suggestionType',
      header: 'Recommendation',
      cell: ({ row }) => {
        return <div>{row.original.suggestionType}</div>;
      },
    },
    {
      accessorKey: 'tooth',
      header: 'Tooth',
      cell: ({ row }) => {
        const suggestionType = row.original.suggestionType;
        const toothLabel = ` ${row.original.tooth}`;
        return (
          <div className="vai-flex vai-items-center vai-gap-2">
            <div className="vai-text-sm vai-text-black">
              <FontAwesomeIcon
                icon={suggestionType === 'SRP' ? faGrid2 : faTooth}
                className="vai-ml-1 vai-h-4 vai-w-4"
              />
              {toothLabel}
            </div>
          </div>
        );
      },
      sortingFn: (a, b) => {
        const aValue = a.original.tooth;
        const bValue = b.original.tooth;

        // Helper functions
        const isSingleLetter = (val: any) =>
          typeof val === 'string' && /^[A-N]$/.test(val);
        const isValNumber = (val: any) =>
          !isNaN(Number(val)) && Number(val) >= 1 && Number(val) <= 32;
        const isTwoCharString = (val: any) =>
          typeof val === 'string' && val.length === 2;

        // Assign sort priority
        const getPriority = (val: any) => {
          if (isSingleLetter(val)) return 0;
          if (isValNumber(val)) return 1;
          if (isTwoCharString(val)) return 2;
          return 3;
        };

        const aPriority = getPriority(aValue);
        const bPriority = getPriority(bValue);
        if (aPriority !== bPriority) return aPriority - bPriority;

        // Within each group, sort appropriately
        if (aPriority === 0) {
          // Single letters A-N
          return aValue.localeCompare(bValue);
        }
        if (aPriority === 1) {
          // Numbers 1-32
          return Number(aValue) - Number(bValue);
        }
        if (aPriority === 2) {
          // Two-char strings
          return aValue.localeCompare(bValue);
        }
        return 0;
      },
    },
    {
      accessorKey: 'normalizedTreatmentCode',
      header: 'Provider Diagnosis',
      cell: ({ row }) => {
        const diagnosisGroupName =
          row.original.diagnosisGroupName === 'enamelRegen'
            ? 'Incipient'
            : row.original.diagnosisGroupName === 'rootCanal'
              ? 'Root Canal'
              : capitalize(row.original.diagnosisGroupName ?? '');
        const diagnosisWithCode = diagnosisGroupName
          ? `${diagnosisGroupName} (${row.original.normalizedTreatmentCode})`
          : row.original.normalizedTreatmentCode;
        return <div>{diagnosisWithCode}</div>;
      },
    },
    {
      accessorKey: 'correlation',
      header: 'Tx Matching',
      cell: ({ row }) => {
        const correlation = row.original.correlation;
        return (
          <div className="vai-flex vai-items-center vai-justify-center vai-h-full">
            {correlation != null ? (
              correlation < 0 ? (
                <FontAwesomeIcon
                  icon={faArrowDown}
                  className="vai-ml-1 vai-h-4 vai-w-4 vai-text-red-500"
                />
              ) : (
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="vai-ml-1 vai-h-4 vai-w-4 vai-text-green-500"
                />
              )
            ) : null}
          </div>
        );
      },
      sortingFn: (a, b) => {
        const aValue = a.original.correlation;
        const bValue = b.original.correlation;

        // null/undefined last
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        // >=0 first, then <0
        const isAPositive = aValue >= 0;
        const isBPositive = bValue >= 0;
        if (isAPositive && !isBPositive) return -1;
        if (!isAPositive && isBPositive) return 1;

        // Both in same group, sort numerically descending
        return bValue - aValue;
      },
      filterFn: (row, _, filterValue) => {
        if (filterValue === undefined) return true;
        const correlation = row.original.correlation;
        return (
          correlation !== undefined && correlation !== null && correlation < 0
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        return (
          <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
            <div
              className={cn(
                'vai-flex vai-w-24 vai-items-center vai-justify-center vai-px-2 vai-py-1 vai-rounded-sm vai-text-sm vai-border-[1px] vai-border-chart-tint-viz-empty',
                row.original.status === 'COMPLETED' &&
                  'vai-bg-chart-normal-1 vai-text-white',
                row.original.status === 'PLANNED' &&
                  'vai-bg-chart-tint-5 vai-text-chart-shade-5',
                row.original.status === 'UNPLANNED' &&
                  'vai-bg-zinc-100 vai-text-zinc-600',
                row.original.status === 'SCHEDULED' &&
                  'vai-bg-chart-tint-2 vai-text-chart-shade-2',
              )}
            >
              {row.original.status[0].toUpperCase() +
                row.original.status.slice(1).toLowerCase()}
            </div>

            {row.original.partner && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger>
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className="vai-h-4 vai-w-4 vai-text-zinc-400"
                    />
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="vai-bg-zinc-800 vai-opacity-90 vai-border-none"
                  >
                    <div className="vai-flex vai-items-center vai-justify-center vai-text-white vai-gap-2">
                      <span className="vai-font-bold">
                        {row.original.partner}
                      </span>
                      <span className="vai-font-light">
                        completed this treatment.
                      </span>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      },
      filterFn: (row, _, filterValue) => {
        if (filterValue === undefined) return true;
        return row.original.status.toLowerCase() === filterValue.toLowerCase();
      },
    },
    {
      accessorKey: 'viewedInVidea',
      header: 'Reviewed AI?',
      cell: ({ row }) => {
        return (
          <div>
            {row.original.viewedInVidea ? (
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="vai-ml-1 vai-h-4 vai-w-4 vai-text-green-500"
              />
            ) : (
              <FontAwesomeIcon
                icon={faCircleMinus}
                className="vai-ml-1 vai-h-4 vai-w-4 vai-text-zinc-400"
              />
            )}
          </div>
        );
      },
      sortingFn: (a, b) => {
        const isAValue = a.original.viewedInVidea;
        const isBValue = b.original.viewedInVidea;
        if (isAValue == null && isBValue == null) return 0;
        if (isAValue == null) return 1;
        if (isBValue == null) return -1;
        if (isAValue === isBValue) return 0;
        return isAValue ? -1 : 1; // true first, then false
      },
      enableSorting: true,
    },
    {
      accessorKey: 'deeplink',
      header: '',
      cell: ({ row }) => <FmxLinkButtonCell url={row.original.deeplink} />,
      enableSorting: false,
    },
  ];

  if (isCurated) {
    columns.unshift({
      accessorKey: 'isCurated',
      header: '',
      maxSize: 50,
      cell: ({ row }) => {
        return (
          <div>
            {row.original.isCurated && (
              <FontAwesomeIcon
                icon={faStar}
                className={cn('vai-h-4 vai-w-4', 'vai-text-tangerine-300')}
              />
            )}
          </div>
        );
      },
      sortingFn: (a, b) => {
        const isACurated = !!a.original.isCurated;
        const isBCurated = !!b.original.isCurated;
        if (isACurated !== isBCurated) {
          return isACurated ? -1 : 1;
        }
        return 0;
      },
      filterFn: (row, _, filterValue) => {
        if (filterValue === undefined) return true;
        return row.original.isCurated === filterValue;
      },
    });
  }
  return columns;
};
